import { CompanyEntity } from "../Objects/Company/CompanyEntity"
import { appendTrackingScript, normalizeTrackingId } from "./trackingUtils"

type PublicMenuTrackingWindow = Window & {
  dataLayer?: unknown[]
  fbq?: (...args: unknown[]) => void
  gtag?: (...args: unknown[]) => void
  [key: string]: unknown
}

const initializedKeys = new Set<string>()

const getTrackingWindow = () => window as unknown as PublicMenuTrackingWindow

const getTrackingKey = (company: CompanyEntity) =>
  [
    company.slug,
    company.publicMenuGoogleTagManagerId,
    company.publicMenuGoogleAnalyticsMeasurementId,
    company.publicMenuGoogleAdsConversionId,
    company.publicMenuMetaPixelId,
  ].join("|")

const getRestaurantDataLayerName = (slug: string) => `opPublicMenuDataLayer_${slug.replace(/[^a-z0-9]/gi, "_")}`

const getRestaurantDataLayer = (slug: string) => {
  const trackingWindow = getTrackingWindow()
  const dataLayerName = getRestaurantDataLayerName(slug)

  if (!Array.isArray(trackingWindow[dataLayerName])) {
    trackingWindow[dataLayerName] = []
  }

  return {
    dataLayerName,
    dataLayer: trackingWindow[dataLayerName] as unknown[],
  }
}

const hasAnyPublicMenuTrackingId = (company: CompanyEntity) =>
  [
    company.publicMenuGoogleTagManagerId,
    company.publicMenuGoogleAnalyticsMeasurementId,
    company.publicMenuGoogleAdsConversionId,
    company.publicMenuMetaPixelId,
  ].some((item) => normalizeTrackingId(item))

const ensureGtag = () => {
  const trackingWindow = getTrackingWindow()

  trackingWindow.dataLayer = trackingWindow.dataLayer || []

  trackingWindow.gtag =
    trackingWindow.gtag ||
    ((...args: unknown[]) => {
      trackingWindow.dataLayer?.push(args)
    })

  trackingWindow.gtag("js", new Date())
}

const ensureMetaPixel = () => {
  const trackingWindow = getTrackingWindow()

  if (trackingWindow.fbq) {
    return
  }

  appendTrackingScript(
    "op-public-menu-meta-pixel-init",
    "",
    document.head,
    `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');`,
  )
}

export const initializePublicMenuTracking = (company: CompanyEntity) => {
  if (!company.publicMenuTrackingEnabled || !hasAnyPublicMenuTrackingId(company)) {
    return
  }

  const trackingKey = getTrackingKey(company)

  if (initializedKeys.has(trackingKey)) {
    return
  }

  const gtmId = normalizeTrackingId(company.publicMenuGoogleTagManagerId)
  const gaId = normalizeTrackingId(company.publicMenuGoogleAnalyticsMeasurementId)
  const adsId = normalizeTrackingId(company.publicMenuGoogleAdsConversionId)
  const metaId = normalizeTrackingId(company.publicMenuMetaPixelId)
  const trackingWindow = getTrackingWindow()

  trackingWindow.dataLayer = trackingWindow.dataLayer || []

  if (gtmId) {
    const { dataLayerName, dataLayer } = getRestaurantDataLayer(company.slug)

    dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
      restaurantSlug: company.slug,
    })

    appendTrackingScript(
      `op-public-menu-gtm-${gtmId}`,
      `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}&l=${encodeURIComponent(dataLayerName)}`,
      document.head,
    )
  }

  if (gaId || adsId) {
    const googleScriptId = gaId ?? adsId ?? ""

    appendTrackingScript(
      `op-public-menu-ga-${googleScriptId}`,
      `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleScriptId)}`,
      document.head,
    )

    ensureGtag()

    if (gaId) {
      trackingWindow.gtag?.("config", gaId, { send_page_view: false })
    }

    if (adsId) {
      trackingWindow.gtag?.("config", adsId)
    }
  }

  if (metaId) {
    ensureMetaPixel()
    trackingWindow.fbq?.("init", metaId)
  }

  initializedKeys.add(trackingKey)
}

export const trackPublicMenuPageView = (company: CompanyEntity, path: string, title: string) => {
  if (!company.publicMenuTrackingEnabled || !hasAnyPublicMenuTrackingId(company)) {
    return
  }

  const trackingWindow = getTrackingWindow()
  const gtmId = normalizeTrackingId(company.publicMenuGoogleTagManagerId)
  const gaId = normalizeTrackingId(company.publicMenuGoogleAnalyticsMeasurementId)
  const metaId = normalizeTrackingId(company.publicMenuMetaPixelId)

  if (gaId) {
    trackingWindow.gtag?.("event", "page_view", {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
      send_to: gaId,
      restaurant_slug: company.slug,
    })
  }

  if (gtmId) {
    const { dataLayer } = getRestaurantDataLayer(company.slug)

    dataLayer.push({
      event: "public_menu_page_view",
      page_path: path,
      page_title: title,
      restaurant_slug: company.slug,
    })
  }

  if (metaId) {
    trackingWindow.fbq?.("trackSingle", metaId, "PageView")
  }
}
